import React from 'react';
import { RegularMetricId } from '../../../../api/types-graphql';
import { ExternalChartComponentProps } from '../../../../common/components/data-viz-containers/common/types';
import { MetricTableViz } from '../../../../common/components/data-viz-containers/metric-table/MetricTableViz';
import { MetricTableVizConfig } from '../../../../common/components/data-viz-containers/metric-table/types';
import { Languages } from '../../../../constants';
import { MetricIdType, SingleValueTimeSelection } from '../../../../types';

interface MidCareerRecruitmentMetricsTableProps {
  timeSelection: SingleValueTimeSelection;
  externalProps: ExternalChartComponentProps;
}

// Helper to create a metric object
const metric = (id: RegularMetricId): MetricIdType => ({
  type: 'RegularMetricId' as const,
  value: id,
});

const MID_CAREER_FILTER = `APPLICATION.CUSTOM_FILTER_1 = '中途'`;

export const MidCareerRecruitmentMetricsTable: React.FC<
  MidCareerRecruitmentMetricsTableProps
> = (props) => {
  const { timeSelection, externalProps } = props;

  const tableConfig: MetricTableVizConfig = {
    title: {
      [Languages.EN]: 'Recruitment Metrics',
      [Languages.JA]: '採用指標',
    },
    headers: [
      { [Languages.EN]: 'Candidates', [Languages.JA]: '候補者数' },
      { [Languages.EN]: 'Pass Rate', [Languages.JA]: '通過率' },
      { [Languages.EN]: 'Rejected Count', [Languages.JA]: '不合格数' },
      { [Languages.EN]: 'Rejected Rate', [Languages.JA]: '不合格率' },
      { [Languages.EN]: 'Withdrawn Count', [Languages.JA]: '辞退数' },
      { [Languages.EN]: 'Withdrawn Rate', [Languages.JA]: '辞退率' },
    ],
    rows: [
      // Row 1: Applications (応募)
      {
        label: {
          [Languages.EN]: 'Applications',
          [Languages.JA]: '人事書類選考',
        },
        cells: [
          metric(RegularMetricId.Pm0252_2ApplicationsCount),
          null, // No pass rate for first stage
          null, // No rejected count for first stage
          null, // No rejected rate for first stage
          null, // No withdrawn count for first stage
          null, // No withdrawn rate for first stage
        ],
      },
      // Row 2: Screening (書類選考)
      {
        label: { [Languages.EN]: 'Screening', [Languages.JA]: '部門書類選考' },
        cells: [
          metric(RegularMetricId.Pm0256_1ApplicationsAtScreeningCount),
          metric(RegularMetricId.Pm0253_1PassthroughAtAppliedPerc),
          metric(RegularMetricId.Pm0254_2RejectedApplicationsAtAppliedCount),
          metric(RegularMetricId.Pm0254_1RejectedApplicationsAtAppliedPerc),
          metric(RegularMetricId.Pm0255_2WithdrawnApplicationsAtAppliedCount),
          metric(RegularMetricId.Pm0255_1WithdrawnApplicationsAtAppliedPerc),
        ],
      },
      // Row 3: 1st Interview (一次面接)
      {
        label: { [Languages.EN]: '1st Interview', [Languages.JA]: '1次面接' },
        cells: [
          metric(RegularMetricId.Pm0260_1ApplicationsAt_1stInterviewCount),
          metric(RegularMetricId.Pm0257_1PassthroughAtScreeningPerc),
          metric(RegularMetricId.Pm0258_2RejectedApplicationsAtScreeningCount),
          metric(RegularMetricId.Pm0258_1RejectedApplicationsAtScreeningPerc),
          metric(RegularMetricId.Pm0259_2WithdrawnApplicationsAtScreeningCount),
          metric(RegularMetricId.Pm0259_1WithdrawnApplicationsAtScreeningPerc),
        ],
      },
      // Row 4: 2nd Interview (二次面接)
      {
        label: { [Languages.EN]: '2nd Interview', [Languages.JA]: '2次面接' },
        cells: [
          metric(RegularMetricId.Pm0264_1ApplicationsAt_2ndInterviewCount),
          metric(RegularMetricId.Pm0261_1PassthroughAt_1stInterviewPerc),
          metric(
            RegularMetricId.Pm0262_2RejectedApplicationsAt_1stInterviewCount
          ),
          metric(
            RegularMetricId.Pm0262_1RejectedApplicationsAt_1stInterviewPerc
          ),
          metric(
            RegularMetricId.Pm0263_2WithdrawnApplicationsAt_1stInterviewCount
          ),
          metric(
            RegularMetricId.Pm0263_1WithdrawnApplicationsAt_1stInterviewPerc
          ),
        ],
      },
      // Row 5: Final Interview (最終面接)
      {
        label: {
          [Languages.EN]: 'Final Interview',
          [Languages.JA]: '3次面接',
        },
        cells: [
          metric(RegularMetricId.Pm0268_1ApplicationsAtFinalInterviewCount),
          metric(RegularMetricId.Pm0265_1PassthroughAt_2ndInterviewPerc),
          metric(
            RegularMetricId.Pm0266_2RejectedApplicationsAt_2ndInterviewCount
          ),
          metric(
            RegularMetricId.Pm0266_1RejectedApplicationsAt_2ndInterviewPerc
          ),
          metric(
            RegularMetricId.Pm0267_2WithdrawnApplicationsAt_2ndInterviewCount
          ),
          metric(
            RegularMetricId.Pm0267_1WithdrawnApplicationsAt_2ndInterviewPerc
          ),
        ],
      },
      // Row 5: Offer (内定)
      {
        label: { [Languages.EN]: 'Offer', [Languages.JA]: '内定' },
        cells: [
          metric(RegularMetricId.Pm0272_1ApplicationsAtOfferCount),
          metric(RegularMetricId.Pm0269_1PassthroughAtFinalInterviewPerc),
          metric(
            RegularMetricId.Pm0270_2RejectedApplicationsAtFinalInterviewCount
          ),
          metric(
            RegularMetricId.Pm0270_1RejectedApplicationsAtFinalInterviewPerc
          ),
          metric(
            RegularMetricId.Pm0271_2WithdrawnApplicationsAtFinalInterviewCount
          ),
          metric(
            RegularMetricId.Pm0271_1WithdrawnApplicationsAtFinalInterviewPerc
          ),
        ],
      },
      // Row 6: Hired (内定承諾)
      {
        label: { [Languages.EN]: 'Hired', [Languages.JA]: '入社' },
        cells: [
          metric(RegularMetricId.Pm0276_1ApplicationsAtHiredCount),
          metric(RegularMetricId.Pm0273_1PassthroughAtOfferPerc),
          metric(RegularMetricId.Pm0274_2RejectedApplicationsAtOfferCount),
          metric(RegularMetricId.Pm0274_1RejectedApplicationsAtOfferPerc),
          metric(RegularMetricId.Pm0275_2WithdrawnApplicationsAtOfferCount),
          metric(RegularMetricId.Pm0275_1WithdrawnApplicationsAtOfferPerc),
        ],
      },
    ],
    timeSelection,
    filters: MID_CAREER_FILTER,
  };

  return (
    <MetricTableViz
      dataConfig={tableConfig}
      externalProps={externalProps}
      persistenceKey="mid-career-recruitment-metrics-table"
    />
  );
};
